/**
 * Hook de autenticacion que combina session store + endpoints.
 *
 * Modelo Plan C (cookie httpOnly):
 * - El token NO vive en el store. Vive en una cookie httpOnly que el
 *   navegador envia automaticamente (no la manipulamos).
 * - signIn: recibe la sesion del backend, la mete al store (sin token)
 *   y la queryClient clear() para forzar re-fetch con el nuevo tenant.
 * - signOut: llama al backend (que limpia la cookie) y luego limpia el store.
 * - switchTo: igual que signIn pero para cambio de tenant (backend rota cookie).
 * - refreshSession: dispara /me manualmente para refrescar permisos.
 *
 * Ver docs/AUTH_COOKIE_API.md.
 */
import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  login as apiLogin,
  logout as apiLogout,
  me as apiMe,
  platformLogin as apiPlatformLogin,
  lookupTenants,
  switchTenantApi,
  type LoginRequest,
} from '@/api/endpoints/auth';
import { useSessionStore } from '@/stores/session';

import type { TenantOption } from '@/types/user';

export interface SignInResult {
  requiresSelection: boolean;
  tenants?: TenantOption[];
}

interface UseAuthResult {
  isAuthenticated: boolean;
  isReady: boolean;
  signIn: (payload: LoginRequest, tenantSlug?: string | null) => Promise<SignInResult>;
  signInPlatform: (payload: LoginRequest) => Promise<void>;
  signOut: () => Promise<void>;
  switchTo: (slug: string) => Promise<void>;
  selectCompany: (slug: string) => Promise<void>;
  refreshSession: () => Promise<void>;
}

/**
 * Hook principal de auth. Lee el session store (que ya no incluye token)
 * y expone helpers para login/logout/switch-tenant + un refreshSession
 * que rehidrata permisos via GET /api/auth/me.
 */
export function useAuth(): UseAuthResult {
  const user = useSessionStore((s) => s.user);
  const permissions = useSessionStore((s) => s.permissions);
  const queryClient = useQueryClient();

  // Sin token: "autenticado" significa "tenemos datos de sesion hidratados".
  // La validacion REAL contra backend ocurre en cada request (401 si cookie expirada).
  const isAuthenticated = Boolean(user);
  const isReady = permissions.size > 0 || !user;

  const refreshSession = useCallback(async () => {
    try {
      const me = await apiMe();
      const currentTenant = useSessionStore.getState().tenant;
      // Si la respuesta de me no trae tenant pero en el store ya teníamos uno activo en sesión de empresa,
      // preservamos el tenant activo para evitar que la UI quede en blanco o 'Cargando sesión...'
      const resolvedTenant = me.tenant ?? currentTenant;

      const currentRoles = useSessionStore.getState().roles;
      const currentPermissions = useSessionStore.getState().permissions;

      const rawRoles = Array.isArray(me.roles)
        ? me.roles.map((r: unknown) =>
            typeof r === 'string' ? r : ((r as { name?: string }).name ?? String(r)),
          )
        : [];
      const resolvedRoles = rawRoles.length > 0 ? rawRoles : currentRoles;

      const resolvedPermissions =
        Array.isArray(me.permissions) && me.permissions.length > 0
          ? me.permissions
          : Array.from(currentPermissions);

      useSessionStore.getState().setSession({
        expiresAt: me.expires_at ?? null,
        user: me.user,
        tenant: resolvedTenant,
        roles: resolvedRoles,
        permissions: resolvedPermissions,
        capabilities: me.capabilities ?? [],
        scopeStatus: me.scope_status ?? 'none',
        scopes: me.scopes ?? {
          branches: [],
          warehouses: [],
          customer_groups: [],
          vendor_of: [],
          branches_count: 0,
          warehouses_count: 0,
          customer_groups_count: 0,
          vendor_of_count: 0,
        },
      });
    } catch {
      // Si falla (401), el interceptor ya limpia la sesion y redirige a /login.
    }
  }, []);

  const signIn = useCallback(
    async (payload: LoginRequest, tenantSlug?: string | null): Promise<SignInResult> => {
      // La cookie httpOnly se emite automaticamente en el response del login.
      // Solo necesitamos guardar el resto en el store.
      const data = await apiLogin(payload, tenantSlug);

      if (data.requires_tenant_selection) {
        useSessionStore.getState().setPendingSelection(data.user, data.tenants ?? []);
        return {
          requiresSelection: true,
          tenants: data.tenants ?? [],
        };
      }

      if (!data.tenant) {
        throw new Error('Sesion de empresa invalida.');
      }

      useSessionStore.getState().setTenant({
        id: data.tenant.id,
        slug: data.tenant.slug,
        name: data.tenant.name,
        is_active: data.tenant.is_active ?? true,
      });

      useSessionStore.getState().setSession({
        expiresAt: data.expires_at ?? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        user: data.user,
        tenant: data.tenant,
        roles: data.roles.map((r: unknown) =>
          typeof r === 'string' ? r : ((r as { name?: string }).name ?? String(r)),
        ),
        permissions: data.permissions,
        capabilities: data.capabilities ?? [],
        scopeStatus: data.scope_status ?? 'none',
        scopes: data.scopes ?? {
          branches: [],
          warehouses: [],
          customer_groups: [],
          vendor_of: [],
          branches_count: 0,
          warehouses_count: 0,
          customer_groups_count: 0,
          vendor_of_count: 0,
        },
      });

      // Invalidamos todas las queries para forzar re-fetch con el nuevo tenant.
      queryClient.clear();

      return {
        requiresSelection: false,
      };
    },
    [queryClient],
  );

  const signInPlatform = useCallback(
    async (payload: LoginRequest) => {
      const data = await apiPlatformLogin(payload);

      useSessionStore.getState().setSession({
        expiresAt: data.expires_at ?? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        user: data.user,
        tenant: null,
        roles: Array.isArray(data.roles)
          ? data.roles.map((r: unknown) =>
              typeof r === 'string' ? r : ((r as { name?: string }).name ?? String(r)),
            )
          : [],
        permissions: data.permissions ?? [],
        capabilities: data.capabilities ?? [],
        scopeStatus: data.scope_status ?? 'none',
        scopes: data.scopes ?? {
          branches: [],
          warehouses: [],
          customer_groups: [],
          vendor_of: [],
          branches_count: 0,
          warehouses_count: 0,
          customer_groups_count: 0,
          vendor_of_count: 0,
        },
      });

      queryClient.clear();
    },
    [queryClient],
  );

  const signOut = useCallback(async () => {
    try {
      // El backend limpia la cookie httpOnly en el response.
      await apiLogout();
    } catch {
      // Ignorar errores de logout (ya limpiamos la sesion local).
    } finally {
      useSessionStore.getState().clearSession();
      queryClient.clear();
    }
  }, [queryClient]);

  const switchTo = useCallback(
    async (slug: string) => {
      const data = await switchTenantApi(slug);
      useSessionStore.getState().setSession({
        expiresAt: data.expires_at,
        user: data.user as never,
        tenant: data.tenant as never,
        roles: data.roles.map((r: unknown) =>
          typeof r === 'string' ? r : ((r as { name?: string }).name ?? String(r)),
        ),
        permissions: data.permissions,
        capabilities: data.capabilities ?? [],
        scopeStatus: data.scope_status === 'deny' ? 'restrict' : data.scope_status,
        scopes: (data.scopes ?? {}) as never,
      });
      queryClient.clear();
    },
    [queryClient],
  );

  const selectCompany = useCallback(
    async (slug: string) => {
      await switchTo(slug);
      useSessionStore.getState().clearPendingTenants();
    },
    [switchTo],
  );

  return {
    isAuthenticated,
    isReady,
    signIn,
    signInPlatform,
    signOut,
    switchTo,
    selectCompany,
    refreshSession,
  };
}

/**
 * Hook que retorna los tenants disponibles para el email del user actual.
 * Usado por el TenantSwitcher en el Topbar.
 */
export interface AvailableTenant {
  id: number;
  name: string;
  slug: string;
  is_active?: boolean;
}

export function useAvailableTenants() {
  const email = useSessionStore((s) => s.user?.email);
  return useQuery<AvailableTenant[]>({
    queryKey: ['auth', 'available-tenants', email],
    queryFn: async () => {
      if (!email) return [];
      const data = await lookupTenants({ email });
      return data;
    },
    enabled: Boolean(email),
    staleTime: 5 * 60 * 1000,
  });
}
